import React, { useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, FileText, Users, CheckSquare, Square, Calendar, AlertCircle } from 'lucide-react'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import './ResearchForms.css'

const ResearchForms = () => {
  const navigate = useNavigate()
  const individualFormRef = useRef(null)
  const groupFormRef = useRef(null)

  const downloadPDF = async (ref, filename) => {
    const element = ref.current;
    
    // Aumentar escala para calidad cristalina (Retina ready)
    const canvas = await html2canvas(element, {
      scale: 3,
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    let heightLeft = imgHeight;
    let position = 0;

    // Página 1
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    // Páginas adicionales si el contenido es demasiado largo
    while (heightLeft > 0) {
      position -= pdfHeight; // Desplazar la imagen hacia arriba
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save(filename);
  }

  return (
    <div className="research-forms-page">
      <div className="forms-header-section">
        <button className="back-button-simple" onClick={() => navigate('/crear-grupo-investigacion')}>
          <ArrowLeft size={20} /> Volver a Instrucciones
        </button>
        <h1>Formularios Oficiales de Investigación</h1>
        <p>Descarga los formatos requeridos para formalizar tu participación o registro de grupo.</p>
        
        <div className="submission-instructions-banner">
          <AlertCircle size={20} className="instruction-icon" />
          <p><strong>Importante:</strong> Cada grupo de investigación debe enviar <strong>1 hoja individual por cada miembro</strong> y <strong>1 hoja de registro de grupo</strong>. Todo debe estar contenido en la misma carpeta para su entrega oficial.</p>
        </div>
      </div>

      <div className="forms-grid-container">
        {/* CARD 1: INDIVIDUAL */}
        <div className="form-card-preview">
          <div className="form-card-icon individual">
            <FileText size={32} />
          </div>
          <h3>Formulario Individual</h3>
          <p>Para estudiantes que desean unirse de forma independiente a un grupo existente.</p>
          <button className="download-btn" onClick={() => downloadPDF(individualFormRef, 'Formulario_Individual_Inscripcion.pdf')}>
            <Download size={18} /> Descargar PDF Individual
          </button>
        </div>

        {/* CARD 2: GROUP */}
        <div className="form-card-preview">
          <div className="form-card-icon group">
            <Users size={32} />
          </div>
          <h3>Registro de Grupo</h3>
          <p>Para fundadores que están registrando un nuevo equipo de investigación oficial.</p>
          <button className="download-btn" onClick={() => downloadPDF(groupFormRef, 'Formulario_Registro_Grupo.pdf')}>
            <Download size={18} /> Descargar PDF de Grupo
          </button>
        </div>
      </div>

      {/* HIDDEN TEMPLATES FOR PDF GENERATION (Rendered high quality but off-screen) */}
      <div style={{ position: 'fixed', left: 0, top: 0, width: '1px', height: '1px', overflow: 'hidden', zIndex: -100, visibility: 'visible' }}>

        
        {/* INDIVIDUAL FORM TEMPLATE */}
        <div ref={individualFormRef} className="pdf-template a4-page">
          <div className="pdf-header">
            <div className="pdf-badge">OFICIAL</div>
            <h1>FORMULARIO INDIVIDUAL DE INSCRIPCIÓN</h1>
            <h2>Grupo de Investigación – Estudiantes de Pregrado</h2>
          </div>

          <div className="pdf-section">
            <h3 className="section-title">1. Datos Personales</h3>
            <div className="pdf-field"><strong>Nombre completo:</strong> <span className="line"></span></div>
            <div className="pdf-field"><strong>Número de identidad / matrícula:</strong> <span className="line"></span></div>
            <div className="pdf-row">
                <div className="pdf-field"><strong>Correo electrónico:</strong> <span className="line"></span></div>
                <div className="pdf-field"><strong>Teléfono:</strong> <span className="line"></span></div>
            </div>
          </div>

          <div className="pdf-section">
            <h3 className="section-title">2. Información Académica</h3>
            <div className="pdf-field"><strong>Universidad:</strong> <span className="line"></span></div>
            <div className="pdf-field"><strong>Carrera:</strong> <span className="line"></span></div>
            <div className="pdf-field">
              <strong>Año / Nivel académico:</strong>
              <div className="checkbox-group">
                <Square size={14} /> 1° &nbsp;&nbsp; <Square size={14} /> 2° &nbsp;&nbsp; <Square size={14} /> 3° &nbsp;&nbsp; <Square size={14} /> 4° &nbsp;&nbsp; <Square size={14} /> 5° &nbsp;&nbsp; Otro: <span className="line small"></span>
              </div>
            </div>
            <div className="pdf-field"><strong>Promedio académico:</strong> <span className="line small"></span></div>
          </div>

          <div className="pdf-section">
            <h3 className="section-title">3. Intereses de Investigación</h3>
            <div className="pdf-field"><strong>Área(s) de interés:</strong> <div className="checkbox-item"><Square size={14} /> Describe: <span className="line"></span></div></div>
            <div className="pdf-field"><strong>¿Por qué deseas unirte a un grupo de investigación?</strong></div>
            <div className="dotted-box" style={{ height: '80px' }}></div>
          </div>

          <div className="pdf-section">
            <h3 className="section-title">4. Experiencia y Habilidades</h3>
            <div className="pdf-field"><strong>¿Tienes experiencia en investigación?</strong> &nbsp;&nbsp; <Square size={14} /> Sí &nbsp;&nbsp; <Square size={14} /> No</div>
            <div className="pdf-field"><strong>Si sí, describe:</strong></div>
            <div className="dotted-box" style={{ height: '60px' }}></div>
            <div className="pdf-field"><strong>Habilidades técnicas (lenguajes, herramientas, etc.):</strong></div>
            <div className="dotted-box" style={{ height: '60px' }}></div>
          </div>

          <div className="pdf-section">
            <h3 className="section-title">5. Disponibilidad</h3>
            <div className="pdf-field">
                <strong>Horas por semana:</strong> &nbsp;&nbsp; <Square size={14} /> 2–4 &nbsp;&nbsp; <Square size={14} /> 5–8 &nbsp;&nbsp; <Square size={14} /> 9–12 &nbsp;&nbsp; <Square size={14} /> Más
            </div>
            <div className="pdf-field">
                <strong>Días disponibles:</strong> &nbsp;&nbsp; <Square size={14} /> Lunes &nbsp;&nbsp; <Square size={14} /> Martes &nbsp;&nbsp; <Square size={14} /> Miércoles &nbsp;&nbsp; <Square size={14} /> Jueves &nbsp;&nbsp; <Square size={14} /> Viernes &nbsp;&nbsp; <Square size={14} /> Sábado
            </div>
          </div>

          <div className="pdf-section">
            <h3 className="section-title">6. Compromiso</h3>
            <div className="pdf-field"><strong>¿Puedes comprometerte al menos un semestre?</strong> &nbsp;&nbsp; <Square size={14} /> Sí &nbsp;&nbsp; <Square size={14} /> No</div>
            <div className="pdf-field"><strong>¿Estás dispuesto a participar activamente?</strong> &nbsp;&nbsp; <Square size={14} /> Sí &nbsp;&nbsp; <Square size={14} /> No</div>
          </div>

          <div className="pdf-footer-sign">
            <p className="declaration">Declaro que la información es verídica.</p>
            <div className="signature-area">
                <div className="sign-block">
                    <div className="sign-line"></div>
                    <p>Firma del Estudiante</p>
                </div>
                <div className="date-block">
                    <p>Fecha: ____ / ____ / ______</p>
                </div>
            </div>
            <div className="pdf-admin-note">
                <p><strong>Nota:</strong> Estos documentos deben ser compartidos con el <strong>Encargado de Investigación</strong> del voluntariado.</p>
            </div>
          </div>
        </div>

        {/* GROUP REGISTRATION FORM TEMPLATE */}
        <div ref={groupFormRef} className="pdf-template a4-page">
          <div className="pdf-header">
            <div className="pdf-badge">OFICIAL</div>
            <h1>FORMULARIO DE REGISTRO DE GRUPO DE INVESTIGACIÓN</h1>
            <h2>Estudiantes de Pregrado</h2>
          </div>

          <div className="pdf-section">
            <h3 className="section-title">1. Información General del Grupo</h3>
            <div className="pdf-field"><strong>Nombre del grupo:</strong> <span className="line"></span></div>
            <div className="pdf-field"><strong>Área principal de investigación:</strong> <span className="line"></span></div>
            <div className="pdf-field"><strong>Línea(s) de investigación:</strong> <span className="line"></span></div>
            <div className="pdf-field"><strong>Objetivo general del grupo:</strong></div>
            <div className="dotted-box" style={{ height: '60px' }}></div>
          </div>

          <div className="pdf-section">
            <h3 className="section-title">2. Integrantes del Grupo</h3>
            <table className="pdf-table">
                <thead>
                    <tr>
                        <th style={{ width: '30px' }}>Nº</th>
                        <th>Nombre Completo</th>
                        <th>Carrera</th>
                        <th style={{ width: '40px' }}>Año</th>
                        <th>Correo</th>
                        <th>Rol</th>
                    </tr>
                </thead>
                <tbody>
                    {[1,2,3,4,5].map(n => (
                        <tr key={n}>
                            <td>{n}</td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td>{n === 1 ? 'Coordinador' : 'Integrante'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
          </div>

          <div className="pdf-section">
            <h3 className="section-title">3. Docente Asesor (si aplica)</h3>
            <div className="pdf-field"><strong>Nombre:</strong> <span className="line"></span></div>
            <div className="pdf-row">
                <div className="pdf-field"><strong>Correo:</strong> <span className="line"></span></div>
                <div className="pdf-field"><strong>Departamento / Facultad:</strong> <span className="line"></span></div>
            </div>
          </div>

          <div className="pdf-section">
            <h3 className="section-title">4. Plan de Trabajo</h3>
            <div className="pdf-field"><strong>Descripción breve del proyecto:</strong></div>
            <div className="dotted-box" style={{ height: '50px' }}></div>
            <div className="pdf-field"><strong>Metodología a utilizar:</strong></div>
            <div className="dotted-box" style={{ height: '40px' }}></div>
            <div className="pdf-field"><strong>Resultados esperados:</strong></div>
            <div className="dotted-box" style={{ height: '40px' }}></div>
          </div>

          <div className="pdf-section">
            <h3 className="section-title">5. Recursos Necesarios</h3>
            <div className="pdf-field"><strong>Software / herramientas:</strong> <span className="line"></span></div>
            <div className="pdf-field"><strong>Equipos:</strong> <span className="line"></span></div>
          </div>

          <div className="pdf-section">
            <h3 className="section-title">6. Cronograma Tentativo</h3>
            <table className="pdf-table mini">
                <thead>
                    <tr>
                        <th>Actividad</th>
                        <th>Mes 1</th>
                        <th>Mes 2</th>
                        <th>Mes 3</th>
                        <th>Mes 4</th>
                    </tr>
                </thead>
                <tbody>
                    <tr><td>Planeación</td><td><Square size={14}/></td><td><Square size={14}/></td><td><Square size={14}/></td><td><Square size={14}/></td></tr>
                    <tr><td>Desarrollo</td><td><Square size={14}/></td><td><Square size={14}/></td><td><Square size={14}/></td><td><Square size={14}/></td></tr>
                    <tr><td>Evaluación</td><td><Square size={14}/></td><td><Square size={14}/></td><td><Square size={14}/></td><td><Square size={14}/></td></tr>
                </tbody>
            </table>
          </div>

          <div className="pdf-footer-sign">
            <p className="declaration">Declaramos que cumpliremos con las actividades y normas del grupo de investigación.</p>
            <div className="signature-area">
                <div className="sign-block">
                    <div className="sign-line"></div>
                    <p>Firma del Coordinador</p>
                </div>
                <div className="date-block">
                    <p>Fecha: ____ / ____ / ______</p>
                </div>
            </div>
            <div className="pdf-admin-note">
                <p><strong>Nota:</strong> Estos documentos deben ser compartidos con el <strong>Encargado de Investigación</strong> del voluntariado.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResearchForms
